import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader } from '@mantine/core';
import {
  AlertCircle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  Cloud,
  IndianRupee,
  Info,
  Percent,
  PieChart as PieChartIcon,
  Server,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BillingFiltersBar } from '../../../components/MasterAdmin/BillingAnalytics/BillingFiltersBar';
import InsightsPageShell from '../../../components/MasterAdmin/insights/InsightsPageShell';
import type { MetricInfoDefinition } from '../../../components/MasterAdmin/ui/MetricInfoPopover';
import OperationsStatCard from '../../../components/MasterAdmin/operations/OperationsStatCard';
import { OpsChartTitle } from '../../../components/MasterAdmin/operations/OpsMetricLabels';
import OperationsInfoTip from '../../../components/MasterAdmin/operations/OperationsInfoTip';
import {
  CHART_COLORS,
  opsChartCard,
  opsMetricGridClass,
  PIE_PALETTE,
} from '../../../components/MasterAdmin/operations/operationsUi';
import {
  getBillingOverview,
  getBillingResources,
  getBillingServices,
  getBillingTrends,
  getTimeRangeDates,
} from '../../../services/MasterAdmin/billingAnalyticsService';

const DATA_START_DATE = new Date('2026-07-06T00:00:00.000Z');

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '12px',
    fontSize: '12px',
  },
  labelStyle: { color: '#9ca3af' },
  itemStyle: { color: '#fff' },
};

const BILLING_PAGE_INFO: MetricInfoDefinition = {
  definition:
    'GCP cloud cost overview from BigQuery billing export: net vs gross spend, credits, service mix, and the resources driving the bill.',
  logic: [
    'Figures come from the Google Cloud Resource Billing Export, not the live Billing Console invoice.',
    'Small differences vs the console are expected due to attribution, credits, freshness, and rounding.',
  ],
  sources: [{ system: 'BigQuery', table: 'GCP billing export', columns: ['cost', 'credits', 'service', 'resource'] }],
};

type ServiceRow = {
  rank: number;
  name: string;
  serviceId?: string;
  usage: string;
  resources: string | number;
  netCost: number;
  grossCost: number;
  credits: number;
  currency: string;
  growth: string;
  share: number;
  color: string;
};

type ResourceRow = {
  rank: number;
  name: string;
  service: string;
  usage: string;
  netCost: number;
  grossCost: number;
  credits: number;
  currency: string;
  share: number;
};

type TrendRow = {
  name: string;
  fullDate: string;
  netCost: number;
  grossCost: number;
  credits: number;
  currency: string;
};

function formatCurrency(value: number, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatCompact(value: number) {
  const abs = Math.abs(value || 0);
  if (abs >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${(value / 1000).toFixed(1)}k`;
  return `₹${(value || 0).toFixed(0)}`;
}

function formatDateShort(value: string | Date) {
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function parseGrowth(growth?: string): { direction: 'up' | 'down' | 'flat' | 'na'; label: string } {
  if (!growth || growth === 'N/A') return { direction: 'na', label: 'N/A' };
  const numeric = Number(String(growth).replace(/[+%]/g, ''));
  if (!Number.isFinite(numeric) || Math.abs(numeric) < 0.05) return { direction: 'flat', label: growth };
  if (String(growth).startsWith('-') || numeric < 0) return { direction: 'down', label: growth };
  return { direction: 'up', label: growth };
}

function GrowthBadge({ growth, valid }: { growth: string; valid: boolean }) {
  if (!valid) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        N/A
      </span>
    );
  }
  const parsed = parseGrowth(growth);
  if (parsed.direction === 'na') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        N/A
      </span>
    );
  }
  if (parsed.direction === 'flat') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        {parsed.label}
      </span>
    );
  }
  const up = parsed.direction === 'up';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        up
          ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
      }`}
    >
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {parsed.label}
    </span>
  );
}

function ShareBar({ share, color }: { share: number; color: string }) {
  return (
    <div className="flex min-w-[88px] items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, share))}%`, backgroundColor: color }} />
      </div>
      <span className="w-10 text-right text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">{share.toFixed(1)}%</span>
    </div>
  );
}

const BillingOverviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const timeRange = searchParams.get('timeRange') || 'This Month';
  const projectId = searchParams.get('projectId') || 'vedika-health';
  const customStart = searchParams.get('startDate') || undefined;
  const customEnd = searchParams.get('endDate') || undefined;
  const serviceId = searchParams.get('serviceId') || undefined;
  const resourceName = searchParams.get('resourceName') || undefined;

  const [overviewData, setOverviewData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spendingTrend, setSpendingTrend] = useState<TrendRow[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    const fetchOverviewData = async () => {
      if (!projectId) return;
      try {
        setIsLoading(true);
        setError(null);
        const { start, end, prevStart, prevEnd } = getTimeRangeDates(timeRange, customStart, customEnd);

        const [overviewRes, trendsData, servicesData, resourcesData] = await Promise.all([
          getBillingOverview(start, end, prevStart, prevEnd, projectId, serviceId, resourceName),
          getBillingTrends(start, end, projectId, serviceId, resourceName),
          getBillingServices(start, end, projectId, serviceId, resourceName),
          getBillingResources(start, end, projectId, 12, serviceId, resourceName),
        ]);

        const totalNet = Number(overviewRes?.netCost || 0);
        const mappedServices: ServiceRow[] = (servicesData || []).map((s: any, idx: number) => ({
          rank: idx + 1,
          name: s.serviceDescription || s.serviceId || 'Unknown service',
          serviceId: s.serviceId,
          usage: s.usage || 'N/A',
          resources: s.resources ?? 'N/A',
          netCost: Number(s.netCost || 0),
          grossCost: Number(s.grossCost || 0),
          credits: Number(s.credits || 0),
          currency: s.currency || overviewRes?.currency || 'INR',
          growth: s.growth || 'N/A',
          share: totalNet > 0 ? (Number(s.netCost || 0) / totalNet) * 100 : 0,
          color: PIE_PALETTE[idx % PIE_PALETTE.length],
        }));

        const mappedResources: ResourceRow[] = (resourcesData || []).map((r: any, idx: number) => ({
          rank: idx + 1,
          name: r.resourceName || r.resourceGlobalName || r.skuDescription || 'Unknown resource',
          service: r.serviceDescription || 'Unknown',
          usage: r.usage || 'N/A',
          netCost: Number(r.netCost || 0),
          grossCost: Number(r.grossCost || 0),
          credits: Number(r.credits || 0),
          currency: r.currency || overviewRes?.currency || 'INR',
          share: totalNet > 0 ? (Number(r.netCost || 0) / totalNet) * 100 : 0,
        }));

        const mappedTrends: TrendRow[] = (trendsData || []).map((t: any) => ({
          name: t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '',
          fullDate: t.date,
          netCost: Number(t.netCost || 0),
          grossCost: Number(t.grossCost || 0),
          credits: Math.abs(Number(t.credits || 0)),
          currency: t.currency || overviewRes?.currency || 'INR',
        }));

        setOverviewData(overviewRes);
        setServices(mappedServices);
        setResources(mappedResources);
        setSpendingTrend(mappedTrends);
      } catch (err) {
        console.error('Failed to fetch overview data:', err);
        setError('Unable to load billing analytics. Please retry or check the backend.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverviewData();
  }, [timeRange, projectId, customStart, customEnd, serviceId, resourceName]);

  const currency = overviewData?.currency || 'INR';
  const hasValidPreviousData = overviewData?.dateRange?.prevStart
    ? new Date(overviewData.dateRange.prevStart) >= DATA_START_DATE
    : false;

  const pieData = useMemo(() => {
    const billed = services.filter((s) => s.netCost > 0);
    const top = billed.slice(0, 6);
    const rest = billed.slice(6).reduce((sum, s) => sum + s.netCost, 0);
    if (rest > 0) {
      top.push({
        rank: 7,
        name: 'Others',
        usage: 'N/A',
        resources: 'N/A',
        netCost: rest,
        grossCost: 0,
        credits: 0,
        currency,
        growth: 'N/A',
        share: overviewData?.netCost > 0 ? (rest / overviewData.netCost) * 100 : 0,
        color: '#94a3b8',
      });
    }
    return top;
  }, [services, currency, overviewData?.netCost]);

  const barData = useMemo(
    () =>
      services
        .filter((s) => s.netCost > 0)
        .slice(0, 8)
        .map((s) => ({
          name: s.name.length > 22 ? `${s.name.slice(0, 20)}…` : s.name,
          fullName: s.name,
          netCost: s.netCost,
          fill: s.color,
        })),
    [services]
  );

  const insights = useMemo(() => {
    const net = Number(overviewData?.netCost || 0);
    const gross = Number(overviewData?.grossCost || 0);
    const credits = Math.abs(Number(overviewData?.credits || 0));
    const days = Math.max(1, spendingTrend.length);
    const avgDaily = net / days;
    const peak = spendingTrend.reduce<TrendRow | null>((best, row) => (!best || row.netCost > best.netCost ? row : best), null);
    const low = spendingTrend
      .filter((row) => row.netCost > 0)
      .reduce<TrendRow | null>((best, row) => (!best || row.netCost < best.netCost ? row : best), null);
    const top3Share = services.slice(0, 3).reduce((sum, s) => sum + s.share, 0);
    const savingsRate = gross > 0 ? (credits / gross) * 100 : 0;
    const growth = hasValidPreviousData ? Number(overviewData?.growth?.netCost) : null;

    const recommendations: Array<{ title: string; detail: string; tone: 'warn' | 'good' | 'info' }> = [];
    if (services[0] && services[0].share >= 40) {
      recommendations.push({
        tone: 'warn',
        title: `${services[0].name} is ${services[0].share.toFixed(0)}% of the bill`,
        detail: 'A single service dominates spend. Review usage, idle resources, and committed-use discounts for this product.',
      });
    }
    if (growth != null && growth >= 20) {
      recommendations.push({
        tone: 'warn',
        title: `Net cost rose ${Math.abs(growth).toFixed(1)}% vs previous period`,
        detail: 'Investigate the services with the largest growth in the table below before the next invoice cycle.',
      });
    } else if (growth != null && growth <= -10) {
      recommendations.push({
        tone: 'good',
        title: `Net cost fell ${Math.abs(growth).toFixed(1)}% vs previous period`,
        detail: 'Spend is trending down. Confirm the drop is from optimizations, not missing export days.',
      });
    }
    if (savingsRate >= 15) {
      recommendations.push({
        tone: 'good',
        title: `Credits offset ${savingsRate.toFixed(1)}% of list price`,
        detail: 'Discounts are meaningfully reducing the invoice. Keep CUDs / promotions mapped to the highest-cost services.',
      });
    }
    if (peak && avgDaily > 0 && peak.netCost > avgDaily * 1.6) {
      recommendations.push({
        tone: 'info',
        title: `Peak day ${formatDateShort(peak.fullDate)} at ${formatCurrency(peak.netCost, currency)}`,
        detail: `That day was ${((peak.netCost / avgDaily - 1) * 100).toFixed(0)}% above the period average. Check batch jobs, traffic spikes, or a new service turning on.`,
      });
    }
    if (recommendations.length === 0 && net > 0) {
      recommendations.push({
        tone: 'info',
        title: 'Spend looks evenly distributed this period',
        detail: 'No single spike or concentration stood out. Use the service and resource tables to keep watching the usual drivers.',
      });
    }

    return { net, gross, credits, days, avgDaily, peak, low, top3Share, savingsRate, growth, recommendations };
  }, [overviewData, spendingTrend, services, hasValidPreviousData, currency]);

  const dailyRows = useMemo(() => {
    const newestFirst = [...spendingTrend].reverse();
    return newestFirst.length > 31 ? newestFirst.slice(0, 14) : newestFirst;
  }, [spendingTrend]);

  const lastUpdated = overviewData?.lastExportTime
    ? new Date(overviewData.lastExportTime).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <InsightsPageShell
      title="Billing Overview"
      description="GCP cost, credits, and the services and resources driving spend."
      info={BILLING_PAGE_INFO}
      actions={
        lastUpdated ? (
          <span className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            Data up to <span className="font-semibold text-gray-800 dark:text-gray-200">{lastUpdated}</span>
          </span>
        ) : null
      }
    >
      {showDisclaimer && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="min-w-0 text-sm text-amber-900 dark:text-amber-100">
            <p className="font-semibold">Billing notice</p>
            <p className="mt-1 opacity-90">
              Powered by the Google Cloud Resource Billing Export (BigQuery). Amounts can differ slightly from the Billing Console due to
              attribution, credits, freshness, and rounding. Use the console for official invoices.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowDisclaimer(false)}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
          >
            Dismiss
          </button>
        </div>
      )}

      <BillingFiltersBar />

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-900/40 dark:bg-red-900/20">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <div className={opsMetricGridClass}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            <div className="h-72 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="flex h-48 items-center justify-center">
            <Loader size="lg" variant="dots" />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {overviewData?.dateRange && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hasValidPreviousData ? (
                <>
                  Comparing {formatDateShort(overviewData.dateRange.start)} – {formatDateShort(overviewData.dateRange.end)} against{' '}
                  {formatDateShort(overviewData.dateRange.prevStart)} – {formatDateShort(overviewData.dateRange.prevEnd)}
                </>
              ) : (
                <>
                  Showing {formatDateShort(overviewData.dateRange.start)} – {formatDateShort(overviewData.dateRange.end)}
                </>
              )}
            </p>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary-600" />
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Period summary</p>
            </div>
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              You spent <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(insights.net, currency)}</span>
              {overviewData?.dateRange && (
                <>
                  {' '}
                  between {formatDateShort(overviewData.dateRange.start)} and {formatDateShort(overviewData.dateRange.end)}
                </>
              )}
              .{services[0] && ` ${services[0].name} accounted for ${services[0].share.toFixed(0)}% of net cost`}
              {services[1] && `, followed by ${services[1].name} (${services[1].share.toFixed(0)}%)`}
              {services[2] && ` and ${services[2].name} (${services[2].share.toFixed(0)}%)`}. Credits saved{' '}
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(insights.credits, currency)}</span>.
              {hasValidPreviousData && insights.growth != null && Number(overviewData?.growth?.prevNetCost) >= 1 ? (
                <>
                  {' '}
                  Versus the previous period, costs{' '}
                  <span className={insights.growth > 0 ? 'font-semibold text-red-500' : 'font-semibold text-emerald-500'}>
                    {insights.growth > 0 ? 'increased' : 'decreased'} by {Math.abs(insights.growth)}%
                  </span>
                  .
                </>
              ) : (
                ' There is not enough previous-period data for a like-for-like comparison yet.'
              )}
            </p>
          </div>

          <div className={opsMetricGridClass}>
            <OperationsStatCard
              label="Net cost"
              infoKey="totalCostNet"
              value={formatCurrency(insights.net, currency)}
              sub={
                hasValidPreviousData && insights.growth != null
                  ? `${insights.growth > 0 ? '↑' : insights.growth < 0 ? '↓' : '→'} ${Math.abs(insights.growth)}% vs previous`
                  : 'Final billed amount'
              }
              icon={IndianRupee}
            />
            <OperationsStatCard
              label="Gross cost"
              infoKey="totalGrossCost"
              value={formatCurrency(insights.gross, currency)}
              sub="Before credits & discounts"
              icon={Wallet}
            />
            <OperationsStatCard
              label="Credits saved"
              infoKey="totalCredits"
              value={formatCurrency(insights.credits, currency)}
              sub={insights.gross > 0 ? `${insights.savingsRate.toFixed(1)}% of list price` : 'Promotions & CUDs'}
              icon={Percent}
              accent="text-emerald-600 dark:text-emerald-400"
            />
            <OperationsStatCard
              label="Avg daily spend"
              infoKey="avgDailyCost"
              value={formatCurrency(insights.avgDaily, currency)}
              sub={`${insights.days} day${insights.days === 1 ? '' : 's'} in range`}
              icon={TrendingUp}
            />
            <OperationsStatCard
              label="Services used"
              infoKey="totalServicesUsed"
              value={String(overviewData?.totalServices ?? services.length)}
              sub="GCP products with spend"
              icon={PieChartIcon}
            />
            <OperationsStatCard
              label="Billable resources"
              infoKey="uniqueResources"
              value={String(overviewData?.totalResources ?? resources.length)}
              sub="Unique resources tracked"
              icon={Boxes}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <InsightChip
              label="Savings rate"
              value={`${insights.savingsRate.toFixed(1)}%`}
              hint="Credits vs gross"
              tone="good"
            />
            <InsightChip
              label="Top 3 concentration"
              value={`${insights.top3Share.toFixed(0)}%`}
              hint="Share from top 3 services"
              tone={insights.top3Share >= 75 ? 'warn' : 'info'}
            />
            <InsightChip
              label="Peak day"
              value={insights.peak ? formatCurrency(insights.peak.netCost, currency) : '—'}
              hint={insights.peak ? formatDateShort(insights.peak.fullDate) : 'No daily data'}
              tone="info"
            />
            <InsightChip
              label="Quietest day"
              value={insights.low ? formatCurrency(insights.low.netCost, currency) : '—'}
              hint={insights.low ? formatDateShort(insights.low.fullDate) : 'No daily data'}
              tone="good"
            />
          </div>

          {insights.recommendations.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                Insights
              </h3>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {insights.recommendations.map((rec) => (
                  <div
                    key={rec.title}
                    className={`rounded-lg border px-3 py-2.5 ${
                      rec.tone === 'warn'
                        ? 'border-amber-200 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/20'
                        : rec.tone === 'good'
                          ? 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/20'
                          : 'border-gray-100 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-900/50'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{rec.title}</p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{rec.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <div className={`${opsChartCard} xl:col-span-3`}>
              <OpsChartTitle title="Spending trend" infoKey="billingTrend" />
              {spendingTrend.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No daily spend in this period</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={spendingTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="billingNetFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART_COLORS.primary} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompact} width={56} />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value: number, name: string) => [formatCurrency(value, currency), name]}
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.fullDate ? formatDateShort(payload[0].payload.fullDate) : ''
                        }
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Area
                        type="monotone"
                        dataKey="netCost"
                        name="Net cost"
                        stroke={CHART_COLORS.primary}
                        fill="url(#billingNetFill)"
                        strokeWidth={2.5}
                      />
                      <Area
                        type="monotone"
                        dataKey="grossCost"
                        name="Gross cost"
                        stroke={CHART_COLORS.purple}
                        fill="transparent"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                      />
                      <Bar dataKey="credits" name="Credits" fill={CHART_COLORS.healthy} maxBarSize={18} radius={[4, 4, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className={`${opsChartCard} xl:col-span-2`}>
              <OpsChartTitle title="Cost distribution" infoKey="costDistribution" />
              {pieData.length === 0 ? (
                <p className="py-16 text-center text-sm text-gray-500">No service spend in this period</p>
              ) : (
                <>
                  <div className="relative h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={58}
                          outerRadius={84}
                          paddingAngle={2}
                          dataKey="netCost"
                          nameKey="name"
                          stroke="none"
                        >
                          {pieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          {...chartTooltipStyle}
                          formatter={(value: number, name: string) => [formatCurrency(value, currency), name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Net</span>
                      <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatCompact(insights.net)}</span>
                    </div>
                  </div>
                  <div className="mt-2 max-h-40 space-y-2 overflow-y-auto">
                    {pieData.map((item) => (
                      <div key={item.name} className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="truncate text-xs font-medium text-gray-600 dark:text-gray-300">{item.name}</span>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xs font-semibold tabular-nums text-gray-900 dark:text-white">
                            {formatCurrency(item.netCost, item.currency)}
                          </p>
                          <p className="text-[10px] text-gray-400">{item.share.toFixed(1)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={opsChartCard}>
            <OpsChartTitle title="Top services by net cost" infoKey="topServicesChart" />
            {barData.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">No service spend in this period</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatCompact} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip
                      {...chartTooltipStyle}
                      formatter={(value: number) => [formatCurrency(value, currency), 'Net cost']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                    />
                    <Bar dataKey="netCost" radius={[0, 6, 6, 0]} maxBarSize={22}>
                      {barData.map((entry) => (
                        <Cell key={entry.fullName} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Service cost breakdown</p>
                <OperationsInfoTip metricKey="topServicesTable" title="Service cost breakdown" />
              </div>
              <Link to="/billing/services" className="shrink-0 text-xs font-semibold text-primary-600 hover:underline">
                View all services →
              </Link>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    {['#', 'Service', 'Usage', 'Resources', 'Gross', 'Credits', 'Net cost', 'Share', 'Growth'].map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                          ['Gross', 'Credits', 'Net cost', 'Growth'].includes(h) ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {services.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                        No services billed in this period
                      </td>
                    </tr>
                  ) : (
                    services.slice(0, 10).map((svc) => (
                      <tr key={`${svc.serviceId}-${svc.rank}`} className="bg-white dark:bg-gray-900/40">
                        <td className="px-4 py-3 text-xs text-gray-400">{svc.rank}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: svc.color }} />
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">{svc.name}</p>
                              <p className="text-[11px] text-gray-400">GCP service</p>
                            </div>
                          </div>
                        </td>
                        <td className="max-w-[180px] truncate px-4 py-3 text-xs text-gray-500" title={svc.usage}>
                          {svc.usage}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{svc.resources}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500">{formatCurrency(svc.grossCost, svc.currency)}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(Math.abs(svc.credits), svc.currency)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(svc.netCost, svc.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <ShareBar share={svc.share} color={svc.color} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <GrowthBadge growth={svc.growth} valid={hasValidPreviousData} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {services.length > 0 && (
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold dark:border-gray-700 dark:bg-gray-800/80">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-500">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(insights.gross, currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(insights.credits, currency)}</td>
                      <td className="px-4 py-3 text-right font-mono text-primary-600">{formatCurrency(insights.net, currency)}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">100%</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Top resources</p>
                <OperationsInfoTip metricKey="topResourcesTable" title="Top resources" />
              </div>
              <Link to="/billing/resources" className="shrink-0 text-xs font-semibold text-primary-600 hover:underline">
                View all resources →
              </Link>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    {['#', 'Resource', 'Service', 'Usage', 'Gross', 'Credits', 'Net cost', 'Share of bill'].map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                          ['Gross', 'Credits', 'Net cost'].includes(h) ? 'text-right' : 'text-left'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {resources.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                        No resources billed in this period
                      </td>
                    </tr>
                  ) : (
                    resources.map((res) => (
                      <tr key={`${res.name}-${res.rank}`} className="bg-white dark:bg-gray-900/40">
                        <td className="px-4 py-3 text-xs text-gray-400">{res.rank}</td>
                        <td className="max-w-[280px] px-4 py-3">
                          <p className="truncate font-mono text-xs font-semibold text-gray-900 dark:text-white" title={res.name}>
                            {res.name}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{res.service}</td>
                        <td className="max-w-[160px] truncate px-4 py-3 text-xs text-gray-500" title={res.usage}>
                          {res.usage}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500">{formatCurrency(res.grossCost, res.currency)}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(Math.abs(res.credits), res.currency)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(res.netCost, res.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <ShareBar share={res.share} color={PIE_PALETTE[(res.rank - 1) % PIE_PALETTE.length]} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Daily cost ledger</p>
                <OperationsInfoTip metricKey="dailyCostTable" title="Daily cost ledger" />
              </div>
              <Link to="/billing/history" className="shrink-0 text-xs font-semibold text-primary-600 hover:underline">
                Full history →
              </Link>
            </div>
            {spendingTrend.length > 31 && (
              <p className="mb-3 text-xs text-gray-500">Showing the latest 14 days. Open billing history for the full range.</p>
            )}
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    {['Date', 'Gross', 'Credits', 'Net cost', 'vs daily avg'].map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                          h === 'Date' ? 'text-left' : 'text-right'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {dailyRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                        No daily rows in this period
                      </td>
                    </tr>
                  ) : (
                    dailyRows.map((row) => {
                      const vsAvg = insights.avgDaily > 0 ? ((row.netCost - insights.avgDaily) / insights.avgDaily) * 100 : 0;
                      const isPeak = insights.peak?.fullDate === row.fullDate;
                      return (
                        <tr key={row.fullDate} className={`bg-white dark:bg-gray-900/40 ${isPeak ? 'bg-amber-50/70 dark:bg-amber-950/20' : ''}`}>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                            {formatDateShort(row.fullDate)}
                            {isPeak && (
                              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                Peak
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-gray-500">{formatCurrency(row.grossCost, row.currency)}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(row.credits, row.currency)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(row.netCost, row.currency)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                vsAvg > 8
                                  ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                  : vsAvg < -8
                                    ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                            >
                              {vsAvg > 0 ? '+' : ''}
                              {vsAvg.toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            <Link
              to="/billing/services"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800"
            >
              <Cloud className="h-3.5 w-3.5" /> Services billing
            </Link>
            <Link
              to="/billing/resources"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800"
            >
              <Server className="h-3.5 w-3.5" /> Resource billing
            </Link>
          </div>
        </div>
      )}
    </InsightsPageShell>
  );
};

function InsightChip({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone: 'warn' | 'good' | 'info';
}) {
  const toneClass =
    tone === 'warn'
      ? 'border-amber-200 dark:border-amber-900/40'
      : tone === 'good'
        ? 'border-emerald-200 dark:border-emerald-900/40'
        : 'border-gray-200 dark:border-gray-700';
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm dark:bg-gray-800 ${toneClass}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    </div>
  );
}

export default BillingOverviewPage;
